import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Settings,
  LogOut,
  Sun,
  Moon,
  User,
  Menu,
  X,
  Crown,
  Shield,
  AlertTriangle,
  FileText,
  ClipboardList,
  Globe,
  Calendar,
  BarChart3,
  Building2,
  UserCheck,
  Users,
  BookOpen,
  Search,
  Presentation,
  GraduationCap,
  Ticket,
  Bell,
  Mail,
  Bot,
  Key,
  Target,
  Star,
  Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { Logo } from './Logo';
import NotificationBell from './NotificationBell';
import { 
  Sheet, 
  SheetContent, 
  SheetTrigger,
  SheetClose 
} from './ui/sheet';

// Sub-navigation items for Data Protection section
export const DATA_PROTECTION_SUB_NAV = [
  { path: '/data-protection', label: 'DSAR & Breaches', icon: UserCheck },
  { path: '/incidents', label: 'Incidents', icon: AlertTriangle },
  { path: '/subprocessors', label: 'Vendors', icon: Building2 },
  { path: '/supplier-journey', label: 'Supplier Onboarding', icon: ClipboardList },
  { path: '/due-diligence', label: 'Due Diligence', icon: ClipboardList },
  { path: '/privacy-tools', label: 'Privacy Tools', icon: Shield },
];

// Sub-navigation items for QMS section
export const QMS_SUB_NAV = [
  { path: '/qms', label: 'Document Control', icon: FileText },
  { path: '/training', label: 'Training', icon: GraduationCap },
];

// Sub-navigation items for User Journeys
export const JOURNEYS_SUB_NAV = [
  { path: '/supplier-journey', label: 'Supplier Onboarding', icon: Building2 },
  { path: '/certification-journey', label: 'Certification', icon: Shield },
  { path: '/incident-journey', label: 'Incident Response', icon: AlertTriangle },
  { path: '/board-journey', label: 'Board Reporting', icon: FileText },
  { path: '/vendor-renewal-journey', label: 'Vendor Renewal', icon: ClipboardList },
  { path: '/ma-target-journey', label: 'M&A Onboarding', icon: Building2 },
];

// Sub-navigation items for Audit section
export const AUDIT_SUB_NAV = [
  { path: '/trust-portal', label: 'Trust Portal', icon: Globe },
  { path: '/auditor-management', label: 'Auditor Portal', icon: Users },
  { path: '/questionnaires', label: 'DDQs', icon: ClipboardList },
  { path: '/framework-builder', label: 'Framework Builder', icon: BookOpen },
  { path: '/custom-frameworks', label: 'Custom Frameworks', icon: Shield },
  { path: '/internal-audit', label: 'Internal Audit', icon: Search },
  { path: '/board-pack', label: 'Board Pack', icon: Presentation },
  { path: '/cde-mapping', label: 'CDE Mapping', icon: Shield },
  { path: '/compliance-query', label: 'Compliance Query', icon: Search },
  { path: '/vulnerability-scanner', label: 'Vuln Scanner', icon: AlertTriangle },
  { path: '/data-export', label: 'Data Export', icon: FileText },
];

// Sub-navigation items for AI Governance section
export const AI_GOVERNANCE_SUB_NAV = [
  { path: '/ai-governance', label: 'Dashboard', icon: Bot },
  { path: '/ai-governance/strategy', label: 'AI Strategy', icon: Target },
  { path: '/ai-governance/stakeholders', label: 'Stakeholders', icon: Users },
  { path: '/ai-governance/systems', label: 'AI Systems', icon: Building2 },
  { path: '/ai-governance/risks', label: 'AI Risks', icon: AlertTriangle },
  { path: '/ai-governance/incidents', label: 'AI Incidents', icon: Shield },
  { path: '/ai-governance/training', label: 'AI Training', icon: GraduationCap },
  { path: '/ai-governance/policies', label: 'AI Policies', icon: FileText },
  { path: '/ai-governance/vendors', label: 'AI Vendors', icon: Building2 },
  { path: '/ai-governance/oversight', label: 'Oversight Matrix', icon: UserCheck },
  { path: '/ai-governance/model-cards', label: 'Model Cards', icon: FileText },
  { path: '/ai-governance/bias-audits', label: 'Bias Audits', icon: Shield },
  { path: '/ai-governance/standards-academy', label: 'Standards Academy', icon: GraduationCap },
];

// Sub-navigation items for Settings section
export const SETTINGS_SUB_NAV = [
  { path: '/account', label: 'Account', icon: User },
  { path: '/team', label: 'Team', icon: Users },
  { path: '/entities', label: 'Entities', icon: Building2 },
  { path: '/support-desk', label: 'Support Desk', icon: Ticket },
  { path: '/training', label: 'Training', icon: GraduationCap },
  { path: '/settings/sso', label: 'SSO / SAML', icon: Key },
  { path: '/admin/support', label: 'Admin Tickets', icon: Ticket, adminOnly: true },
  { path: '/admin/framework-builder', label: 'Framework Builder', icon: Shield, adminOnly: true },
  { path: '/admin/leads', label: 'Marketing Leads', icon: Mail, adminOnly: true },
  { path: '/admin/notifications', label: 'Notifications', icon: Bell, adminOnly: true },
];

// Tier badge component - Updated for Standard/Pro/Pro+ tiers
const TierBadge = ({ tier, isFoundingMember = false }) => {
  // Normalize tier name
  const normalizedTier = tier?.toLowerCase().replace('_monthly', '').replace('_annual', '') || 'standard';
  
  const tierStyles = {
    free: 'bg-muted text-muted-foreground border-border',
    trial: 'bg-primary/10 text-primary border-primary/20',
    lite: 'bg-slate-500/15 text-slate-400 border-slate-500/25',
    standard: 'bg-slate-500/15 text-slate-300 border-slate-500/25',
    starter: 'bg-primary/15 text-primary border-primary/25',
    pro: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    professional: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    pro_plus: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    enterprise: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  };
  
  const tierLabels = {
    free: 'Free',
    trial: 'Trial',
    lite: 'Standard',
    standard: 'Standard',
    starter: 'Starter',
    pro: 'Pro',
    professional: 'Pro',
    pro_plus: 'Pro+',
    enterprise: 'Pro+',
  };
  
  const style = tierStyles[normalizedTier] || tierStyles.standard;
  const label = tierLabels[normalizedTier] || 'Standard';
  
  return (
    <div className="flex items-center gap-1.5">
      <span className={`px-2.5 py-1 text-xs font-medium rounded-full border flex items-center gap-1.5 ${style}`}>
        <Crown className="h-3 w-3" />
        {label}
      </span>
      {isFoundingMember && (
        <span className="px-2 py-1 text-xs font-medium rounded-full border bg-amber-500/20 text-amber-400 border-amber-500/30 flex items-center gap-1">
          <Star className="h-3 w-3" />
          FM
        </span>
      )}
    </div>
  );
};

/**
 * MainNavigation - Simplified header with right-aligned items
 * 
 * Layout: [Hamburger (mobile)] Logo (left) | Tier, Theme Toggle, Account, Settings (right)
 */
export const MainNavigation = ({ 
  user, 
  subscription, 
  theme, 
  onToggleTheme, 
  onLogout,
  mobileMenuButton
}) => {
  const navigate = useNavigate();
  
  return (
    <nav className="border-b border-white/[0.08] bg-[#020408]/80 backdrop-blur-2xl sticky top-0 z-50" data-testid="main-navigation">
      <div className="w-full px-3 sm:px-4 lg:px-6">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Left side - Mobile Hamburger + Logo */}
          <div className="flex items-center">
            {mobileMenuButton}
            <Link to="/dashboard" className="flex items-center">
              <Logo className="h-6 sm:h-7" />
            </Link>
          </div>
          
          {/* Right side - All items right-aligned */}
          <div className="flex items-center gap-1.5 sm:gap-3">
            {/* Tier Badge */}
            <Link to="/pricing" className="hidden sm:block hover:opacity-80 transition-opacity">
              <TierBadge tier={subscription?.tier} isFoundingMember={subscription?.is_founding_member} />
            </Link>
            
            {/* Theme Toggle */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onToggleTheme} 
              data-testid="theme-toggle-btn"
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className="h-9 w-9"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            
            {/* Notification Bell */}
            <NotificationBell />
            
            {/* Account / User Info */}
            <Link to="/account" className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/5 transition-colors">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="h-7 w-7 rounded-full" />
              ) : (
                <div className="h-7 w-7 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary" />
                </div>
              )}
              <span className="text-sm font-medium" data-testid="user-name">
                {user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'User'}
              </span>
            </Link>
            
            {/* Settings */}
            <Link to="/account">
              <Button 
                variant="ghost" 
                size="icon" 
                title="Settings"
                className="h-9 w-9 hidden sm:inline-flex"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </Link>
            
            {/* Logout */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onLogout} 
              data-testid="logout-btn" 
              title="Logout"
              className="h-9 w-9 text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <LogOut className="h-4 w-4" />
            </Button>
            
            {/* Mobile Menu */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="sm:hidden h-9 w-9">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] bg-[#0a0f1a] border-slate-800 p-0">
                <div className="flex flex-col h-full">
                  {/* Mobile Header */}
                  <div className="p-4 border-b border-slate-800">
                    <div className="flex items-center justify-between">
                      <Logo className="h-6" />
                      <SheetClose asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <X className="h-4 w-4" />
                        </Button>
                      </SheetClose>
                    </div>
                  </div>
                  
                  {/* User Info */}
                  <div className="p-4 border-b border-slate-800">
                    <div className="flex items-center gap-3">
                      {user?.picture ? (
                        <img src={user.picture} alt={user.name} className="h-10 w-10 rounded-full" />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{user?.name || 'User'}</p>
                        <p className="text-xs text-slate-400">{user?.email}</p>
                      </div>
                    </div>
                    <div className="mt-3">
                      <TierBadge tier={subscription?.tier} isFoundingMember={subscription?.is_founding_member} />
                    </div>
                  </div>
                  
                  {/* Mobile Actions */}
                  <div className="flex-1 p-4 space-y-2">
                    <SheetClose asChild>
                      <Link to="/account" className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors">
                        <Settings className="h-5 w-5 text-slate-400" />
                        <span>Settings</span>
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link to="/pricing" className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors">
                        <Crown className="h-5 w-5 text-primary" />
                        <span>Upgrade Plan</span>
                      </Link>
                    </SheetClose>
                    <button
                      onClick={onToggleTheme}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors"
                    >
                      {theme === 'dark' ? <Sun className="h-5 w-5 text-slate-400" /> : <Moon className="h-5 w-5 text-slate-400" />}
                      <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
                    </button>
                  </div>
                  
                  {/* Logout */}
                  <div className="p-4 border-t border-slate-800">
                    <Button 
                      variant="ghost" 
                      className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={onLogout}
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      Logout
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Keep SubNavigation for secondary navigation
export const SubNavigation = ({ items, rightContent }) => {
  const location = useLocation();
  
  return (
    <div className="border-b border-border bg-card/30">
      <div className="max-w-[90rem] mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-10">
          <div className="flex items-center gap-1">
            {items.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-7 px-3 text-xs ${
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {Icon && <Icon className="h-3 w-3 mr-1.5" />}
                    {item.label}
                  </Button>
                </Link>
              );
            })}
          </div>
          {rightContent}
        </div>
      </div>
    </div>
  );
};

// Kept exports for backwards compatibility
export { TierBadge };
export default MainNavigation;
